import type { ObraDocument } from '@obras/shared';
import {
  extensionOf,
  FILE_KIND_META,
  formatBytes,
  formatDate,
  formatDateTime,
} from '@obras/shared';
import {
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  CalendarDays,
  ClipboardList,
  FileText,
  HardDrive,
  History,
  Paperclip,
  Pencil,
  Plus,
  SearchX,
  Trash2,
  Upload,
  UserRound,
} from 'lucide-react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import { useMemo, useRef, useState, type FormEvent } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardHeader } from '../../components/ui/Card.js';
import { SearchInput, TextAreaField, TextField } from '../../components/ui/Field.js';
import { EmptyState, ErrorState, FormError, Skeleton } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { normalizarBusca } from '../../lib/texto.js';
import { uploadMedia } from '../reports/api.js';
import { useCreateDocument, useDeleteDocument, useDocuments, useUpdateDocument } from './api.js';
import { AcaoDaPonta, InfoDoArquivo, LinhaDeArquivo, Selo } from './Arquivo.js';

const ICON = { strokeWidth: 1.5 } as const;

/** O campo de arquivo: um botão que abre o seletor e mostra o escolhido. */
function EscolherArquivo({
  file,
  onChoose,
  label,
  vazio,
}: {
  file: File | null;
  onChoose: (escolhido: File | null) => void;
  label: string;
  vazio: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div>
      <span className="mb-1.5 block text-[13px] font-medium">{label}</span>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        onChange={(event) => onChoose(event.target.files?.[0] ?? null)}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex w-full items-center gap-3 bg-sunken px-4 py-3 text-left transition-colors hover:bg-sunken-2"
      >
        <span className="grid size-9 shrink-0 place-items-center bg-surface">
          {file ? (
            <span className="font-mono text-[10px] tracking-wide text-ink-3 uppercase">
              {extensionOf(file.name)}
            </span>
          ) : (
            <Paperclip {...ICON} size={16} className="text-ink-3" aria-hidden />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium">
            {file ? file.name : 'Escolher arquivo'}
          </span>
          <span className="block text-[12px] text-ink-3">
            {file ? formatBytes(file.size) : vazio}
          </span>
        </span>
      </button>
    </div>
  );
}

/**
 * Um anexo do dossiê: a mesma linha de arquivo dos relatórios
 * (`<LinhaDeArquivo>`) — o tipo à esquerda, o título e os detalhes na faixa
 * clara, ver e baixar na ponta, e a linha inteira abre o arquivo. Aqui a ponta
 * ganha o histórico, editar e retirar.
 */
function Linha({
  documento,
  obraId,
  onEditar,
  onRemover,
  podeEditar,
}: {
  documento: ObraDocument;
  obraId: string;
  onEditar: () => void;
  onRemover: () => void;
  podeEditar: boolean;
}) {
  const [historico, setHistorico] = useState(false);
  const versoes = documento.previousVersions.length;

  return (
    <li>
      <LinhaDeArquivo
        obraId={obraId}
        documentId={documento.id}
        originalName={documento.originalName}
        sizeBytes={documento.sizeBytes}
        url={documento.url}
        titulo={documento.title}
        /*
         * Na linha, só o que diz o que o anexo é: o título e, embaixo, de que
         * relatório veio ou a descrição. O resto — quem, quando, quanto, com
         * que nome de arquivo — vai para a tira, cada dado com o seu ícone, na
         * mesma ordem em todo anexo.
         */
        detalhes={
          documento.report ? (
            <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-ink-2">
              <ClipboardList {...ICON} size={13} className="shrink-0" aria-hidden />
              <span className="truncate">
                Relatório {documento.report.title} · visita de{' '}
                {formatDate(documento.report.visitDate)}
              </span>
            </span>
          ) : documento.description ? (
            <span className="truncate text-[12px] text-ink-2">{documento.description}</span>
          ) : (
            <span className="text-[12px] text-ink-3">Anexado pela aba</span>
          )
        }
        rodape={
          <>
            <InfoDoArquivo icone={UserRound} rotulo="Enviado por" className="sm:w-40">
              {documento.uploadedBy?.name ?? 'Usuário removido'}
            </InfoDoArquivo>
            <InfoDoArquivo icone={CalendarDays} rotulo="Enviado em" className="sm:w-36">
              {formatDateTime(documento.createdAt)}
            </InfoDoArquivo>
            <InfoDoArquivo icone={HardDrive} rotulo="Tamanho" className="sm:w-24 sm:justify-end">
              {formatBytes(documento.sizeBytes)}
            </InfoDoArquivo>
          </>
        }
        acoes={
          <>
            {versoes > 0 ? (
              <AcaoDaPonta
                rotulo={
                  versoes === 1
                    ? `1 versão anterior de ${documento.title}`
                    : `${versoes} versões anteriores de ${documento.title}`
                }
                ativa={historico}
                onClick={() => setHistorico((aberto) => !aberto)}
              >
                <span className="relative">
                  <History {...ICON} size={16} aria-hidden />
                  <span className="tabular absolute -top-1.5 -right-2 grid h-3.5 min-w-3.5 place-items-center bg-on-plate px-0.5 text-[9px] leading-none font-semibold text-plate">
                    {versoes}
                  </span>
                </span>
              </AcaoDaPonta>
            ) : null}

            {/*
             * O arquivo que veio de um relatório não se edita nem se retira aqui:
             * quem responde por ele é o relatório. Sem os botões, a linha diz
             * isso sozinha.
             */}
            {podeEditar && !documento.report ? (
              <>
                <AcaoDaPonta rotulo={`Editar ${documento.title}`} onClick={onEditar}>
                  <Pencil {...ICON} size={16} aria-hidden />
                </AcaoDaPonta>
                <AcaoDaPonta rotulo={`Retirar ${documento.title}`} onClick={onRemover} perigo>
                  <Trash2 {...ICON} size={16} aria-hidden />
                </AcaoDaPonta>
              </>
            ) : null}
          </>
        }
      />

      {/*
       * As versões trocadas, logo abaixo, recuadas até a faixa clara: o nome
       * riscado em vermelho diz, antes do clique, que não é o arquivo em vigor.
       * Continuam abertas porque podem estar citadas num relatório publicado.
       */}
      {historico ? (
        <ul className="mt-px ml-12 flex flex-col gap-px">
          {documento.previousVersions.map((versao) => (
            <li key={versao.id}>
              <LinhaDeArquivo
                obraId={obraId}
                documentId={versao.id}
                originalName={versao.originalName}
                sizeBytes={versao.sizeBytes}
                url={versao.url}
                substituido
                detalhes={
                  <span className="tabular truncate text-[12px] text-ink-3">
                    {formatBytes(versao.sizeBytes)} · substituído em{' '}
                    {formatDateTime(versao.replacedAt)}
                  </span>
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Onde a busca procura: o que a linha mostra, e o que se lembraria de digitar. */
const textoDeBusca = (documento: ObraDocument): string =>
  normalizarBusca(
    [
      documento.title,
      documento.originalName,
      documento.description ?? '',
      documento.extension,
      documento.report?.title ?? '',
      documento.uploadedBy?.name ?? '',
    ].join(' '),
  );

/** A lista entra subindo um fio a cada troca de ordem, como a grade da galeria. */
const LISTA: Variants = {
  oculta: { opacity: 0, y: 6 },
  visivel: { opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT_QUINT } },
};

/**
 * O mesmo formulário serve para anexar e para editar.
 *
 * Editando, o arquivo é opcional: sem escolher outro, só título e descrição
 * mudam. Escolhendo, o anterior **não é apagado** — vira versão anterior, e
 * continua acessível na lista.
 */
function AnexoModal({
  obraId,
  open,
  editando,
  onClose,
}: {
  obraId: string;
  open: boolean;
  editando: ObraDocument | null;
  onClose: () => void;
}) {
  const create = useCreateDocument(obraId);
  const update = useUpdateDocument(obraId);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pronto, setPronto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Carrega os valores do anexo em edição na primeira renderização com ele.
  if (open && !pronto) {
    setTitle(editando?.title ?? '');
    setDescription(editando?.description ?? '');
    setPronto(true);
  }

  function fechar(): void {
    setFile(null);
    setTitle('');
    setDescription('');
    setPronto(false);
    setFormError(null);
    onClose();
  }

  function escolher(escolhido: File | null): void {
    setFile(escolhido);
    setFormError(null);

    // O nome do arquivo já é um título razoável; quem quiser, troca.
    if (escolhido && title.trim() === '') {
      setTitle(escolhido.name.replace(/\.[^.]+$/, '').slice(0, 180));
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);

    if (!editando && !file) {
      setFormError('Escolha um arquivo.');
      return;
    }

    setEnviando(true);
    try {
      /*
       * O arquivo sobe pela rota de mídia, que conhece a política de extensões
       * e o limite do órgão; só então é vinculado à obra. Separar assim é o que
       * permite recusar o arquivo antes de criar um registro pela metade.
       */
      const mediaId = file ? (await uploadMedia(file, 'documentos')).id : undefined;

      if (editando) {
        await update.mutateAsync({
          documentId: editando.id,
          body: {
            title: title.trim(),
            description: description.trim() === '' ? undefined : description.trim(),
            ...(mediaId ? { mediaId } : {}),
          },
        });
      } else if (mediaId) {
        await create.mutateAsync({
          mediaId,
          title: title.trim(),
          ...(description.trim() === '' ? {} : { description: description.trim() }),
        });
      }

      fechar();
    } catch (erro) {
      setFormError(erro instanceof ApiError ? erro.message : 'Não foi possível enviar o arquivo.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={editando ? 'Editar anexo' : 'Anexar arquivo'}
      description={
        editando ? 'Trocar o arquivo guarda o anterior como versão.' : 'Anexos relacionados à obra.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={fechar} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="anexo-form"
            type="submit"
            loading={enviando}
            icon={<Upload {...ICON} size={16} />}
          >
            {editando ? 'Salvar' : 'Anexar'}
          </Button>
        </>
      }
    >
      <form
        id="anexo-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError>{formError}</FormError>

        {editando ? (
          <div className="flex items-center gap-3 bg-sunken px-4 py-3">
            <Selo extension={editando.extension} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-medium">
                {editando.originalName}
              </span>
              <span className="block text-[12px] text-ink-3">Arquivo atual</span>
            </span>
          </div>
        ) : null}

        <EscolherArquivo
          file={file}
          onChoose={escolher}
          label={editando ? 'Substituir o arquivo' : 'Arquivo'}
          vazio={editando ? 'Deixe em branco para manter o atual' : 'Nenhum arquivo escolhido'}
        />

        <TextField
          label="Título"
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />

        <TextAreaField
          label="Descrição"
          rows={3}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          hint="Opcional."
        />
      </form>
    </Modal>
  );
}

/**
 * Uma aba para documento e arquivo técnico.
 *
 * São a mesma coisa do ponto de vista do dossiê — o que muda é o formato —, e
 * duas abas obrigariam quem procura o memorial descritivo a adivinhar em qual
 * das duas ele foi parar. A separação acontece dentro, por seção, e sai da
 * extensão do arquivo: nenhuma escolha manual de quem anexa.
 */
export function ObraFilesTab({ obraId }: { obraId: string }) {
  const { can } = useAuth();
  const { data, isPending, isError, error, refetch } = useDocuments(obraId);
  const remove = useDeleteDocument(obraId);

  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<ObraDocument | null>(null);
  const [confirmar, setConfirmar] = useState<ObraDocument | null>(null);
  const [busca, setBusca] = useState('');
  const [recentesPrimeiro, setRecentesPrimeiro] = useState(true);

  /*
   * Busca e ordem na tela, sobre o dossiê inteiro: a obra tem dezenas de
   * anexos, não milhares, e eles já vieram todos. As seções por família ficam;
   * a que não sobra nada na busca some.
   */
  const termo = normalizarBusca(busca.trim());
  const grupos = useMemo(() => {
    if (!data) return [];
    const sentido = recentesPrimeiro ? -1 : 1;
    return data.groups
      .map((grupo) => ({
        kind: grupo.kind,
        items: grupo.items
          .filter((documento) => termo === '' || textoDeBusca(documento).includes(termo))
          .sort((a, b) => sentido * a.createdAt.localeCompare(b.createdAt)),
      }))
      .filter((grupo) => grupo.items.length > 0);
  }, [data, termo, recentesPrimeiro]);
  const encontrados = grupos.reduce((soma, grupo) => soma + grupo.items.length, 0);

  /*
   * A tela mostra os botões a quem provavelmente pode. Quem decide de verdade é
   * o servidor, que enxerga a designação da obra e responde 403 a quem não é da
   * equipe — aqui não há como saber isso sem uma consulta a mais.
   */
  const podeEditar = can('fiscal');

  const anexar = podeEditar ? (
    <Button variant="primary" icon={<Plus {...ICON} size={16} />} onClick={() => setNovo(true)}>
      Anexar arquivo
    </Button>
  ) : null;

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : 'Erro desconhecido.'}
        onRetry={() => void refetch()}
      />
    );
  }

  if (isPending) return <Skeleton className="h-64 rounded-none" />;

  return (
    <div className="flex flex-col gap-4">
      {data.total === 0 ? (
        <Card square>
          <EmptyState
            icon={Paperclip}
            title="Nenhum anexo"
            description="Anexos da obra e arquivos enviados nos relatórios."
            {...(anexar ? { action: anexar } : {})}
          />
        </Card>
      ) : (
        <>
          {/*
           * A barra da aba: a busca ocupa o que sobra, a ordem é o mesmo botão
           * da galeria, e "Anexar arquivo" fica na ponta. No celular, a busca
           * desce para uma linha só dela.
           */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1 basis-64">
              <SearchInput
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                onClear={() => setBusca('')}
                placeholder="Buscar por título, arquivo, descrição ou relatório"
                aria-label="Buscar nos anexos"
              />
            </div>
            <Button
              onClick={() => setRecentesPrimeiro((atual) => !atual)}
              icon={
                recentesPrimeiro ? (
                  <ArrowDownWideNarrow {...ICON} size={15} />
                ) : (
                  <ArrowUpNarrowWide {...ICON} size={15} />
                )
              }
            >
              {recentesPrimeiro ? 'Mais recentes' : 'Mais antigos'}
            </Button>
            {anexar}
          </div>

          <p className="tabular text-[13px] text-ink-3" aria-live="polite">
            {termo === ''
              ? data.total === 1
                ? '1 anexo'
                : `${data.total} anexos`
              : encontrados === 0
                ? `Nenhum dos ${data.total} anexos corresponde à busca`
                : `${encontrados} de ${data.total} ${data.total === 1 ? 'anexo' : 'anexos'}`}
          </p>

          {grupos.length === 0 ? (
            <Card square>
              <EmptyState
                icon={SearchX}
                title="Nenhum anexo encontrado"
                description={`Nada no dossiê corresponde a “${busca.trim()}”. A busca olha o título, o nome do arquivo, a descrição, o relatório de origem e quem enviou.`}
                action={
                  <Button size="sm" onClick={() => setBusca('')}>
                    Limpar busca
                  </Button>
                }
              />
            </Card>
          ) : (
            /*
             * Chaveada pela ordem: inverter troca a lista inteira, que entra
             * subindo um fio. A busca filtra no lugar, sem animar a cada tecla.
             */
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={recentesPrimeiro ? 'recentes' : 'antigos'}
                variants={LISTA}
                initial="oculta"
                animate="visivel"
                exit={{ opacity: 0, transition: { duration: 0.1 } }}
                className="flex flex-col gap-4"
              >
                {grupos.map((grupo) => (
                  <Card square key={grupo.kind}>
                    <CardHeader
                      title={FILE_KIND_META[grupo.kind].label}
                      description={FILE_KIND_META[grupo.kind].hint}
                      action={
                        <span className="tabular shrink-0 text-[13px] text-ink-3">
                          {grupo.items.length}
                        </span>
                      }
                    />
                    <ul className="flex flex-col gap-2 px-5 pb-5">
                      {grupo.items.map((documento) => (
                        <Linha
                          key={documento.id}
                          documento={documento}
                          obraId={obraId}
                          podeEditar={podeEditar}
                          onEditar={() => setEditando(documento)}
                          onRemover={() => setConfirmar(documento)}
                        />
                      ))}
                    </ul>
                  </Card>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </>
      )}

      <AnexoModal
        obraId={obraId}
        open={novo || editando !== null}
        editando={editando}
        onClose={() => {
          setNovo(false);
          setEditando(null);
        }}
      />

      <Modal
        open={confirmar !== null}
        onClose={() => setConfirmar(null)}
        title="Retirar do dossiê"
        description="O anexo sai da lista, com todas as suas versões. O histórico e a auditoria são preservados."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmar(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => {
                if (!confirmar) return;
                remove.mutate(confirmar.id, { onSuccess: () => setConfirmar(null) });
              }}
            >
              Retirar
            </Button>
          </>
        }
      >
        <p className="flex items-center gap-3 bg-sunken p-4 text-[13px]">
          <FileText {...ICON} size={16} className="shrink-0 text-ink-3" aria-hidden />
          {confirmar?.title}
        </p>
      </Modal>
    </div>
  );
}
